import React from 'react';
import { Banknote, DollarSign, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';

/**
 * Apply Manual Payment dialog. Fully controlled — parent owns all state.
 */
const ManualPaymentDialog = ({
  open,
  onOpenChange,
  invoice,
  paymentTypes,
  paymentAmount,
  setPaymentAmount,
  paymentType,
  setPaymentType,
  paymentComments,
  setPaymentComments,
  applyingPayment,
  onApplyPayment,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md" data-testid="manual-payment-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Banknote className="h-5 w-5 text-green-600" />
          Apply Manual Payment
        </DialogTitle>
        <DialogDescription>
          Record an offline payment for invoice {invoice?.invoice_number}
        </DialogDescription>
      </DialogHeader>
      {invoice && (
        <div className="space-y-4 pt-2">
          <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-500">Client</span>
              <span className="font-medium">{invoice.client_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Invoice Total</span>
              <span className="font-medium">${(invoice.amount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Remaining Balance</span>
              <span className="font-bold text-amber-700">
                ${(invoice.amount_remaining ?? invoice.amount).toFixed(2)}
              </span>
            </div>
          </div>

          <div>
            <Label htmlFor="payment-amount">Payment Amount *</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="payment-amount"
                data-testid="payment-amount-input"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="payment-type">Payment Type *</Label>
            <Select value={paymentType} onValueChange={setPaymentType}>
              <SelectTrigger className="mt-1" data-testid="payment-type-select">
                <SelectValue placeholder="Select payment type" />
              </SelectTrigger>
              <SelectContent>
                {paymentTypes.map((pt) => (
                  <SelectItem key={pt.value} value={pt.value} data-testid={`payment-type-${pt.value}`}>
                    {pt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="payment-comments">Comments (optional)</Label>
            <Textarea
              id="payment-comments"
              data-testid="payment-comments-input"
              placeholder="e.g., Check #1234, received on 12/15..."
              rows={2}
              value={paymentComments}
              onChange={(e) => setPaymentComments(e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-payment-btn"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={onApplyPayment}
              disabled={applyingPayment}
              data-testid="submit-payment-btn"
            >
              {applyingPayment
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Banknote className="h-4 w-4 mr-2" />}
              {applyingPayment ? 'Applying...' : 'Apply Payment'}
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default ManualPaymentDialog;
